import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { logError } from '@/lib/logger';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      // Not authenticated — expected for logged-out users; log other errors for debugging
      if (err?.response?.status && err.response.status !== 401) {
        logError('checkAuth failed:', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      logError('logout request failed (clearing local state anyway):', err);
    }
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, setUser, loading, logout, checkAuth }),
    [user, loading, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
